import React, { useMemo, useState } from 'react';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { UPDATE_URL } from './CveActions';
import { ACTIONS, filterRows, policyActions, policyRows, statusOf } from './deviceInfo';
import { queueRemovals } from './driftRemove';
import { INSTALL_URL } from './SoftwareBlade';
import { SortHeader, useSort } from './sortable';

const list = (v) => (Array.isArray(v) ? v : []);
const SENTENCE = {
  install: (r, m) => `${r.shown} will be installed on ${m}.`,
  update: (r, m) => `${r.shown} will be updated on ${m}${r.available ? ` to ${r.available}` : ''}.`,
  remove: (r, m) => `Version ${r.version} of ${r.shown} will be uninstalled from ${m}. Data the program keeps elsewhere is not removed.`,
};
const labelOf = (action, r) => `${ACTIONS[action]} ${r.shown}${action !== 'install' && r.version ? ` ${r.version}` : ''}`;

// The device's software policy: every program a rule names, the state it is in, and what can be done to bring it to what the policy says
// (or, for an allowed program, to install, update or uninstall it). Alphabetical until a heading is chosen. Each action asks first; the page sends
// the program and the machine, never a script.
export default function PolicyTab({ state, machine, account, request }) {
  const [typed, setTyped] = useState('');
  const [acting, setActing] = useState(null);       // { row, action } waiting for the go-ahead
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const { sort, toggle, apply } = useSort();
  const rows = useMemo(() => policyRows(state.programs), [state.programs]);
  const shown = useMemo(() => filterRows(rows, { text: typed }), [rows, typed]);
  const ordered = apply(shown, {
    name: { get: (r) => r.shown },
    policy: { get: (r) => r.policy },
    status: { get: (r) => statusOf(r.status).rank },
    version: { get: (r) => (r.installed ? r.version : '') },
    available: { get: (r) => r.available },
  });

  const run = async ({ row, action }) => {
    setBusy(true);
    setOutcome(null);
    try {
      const answer = action === 'remove'
        ? await queueRemovals(request, account, 'apply', [{ deviceIds: [machine.deviceId], name: row.name, version: row.version }])
        : (await request('POST', action === 'update' ? UPDATE_URL : INSTALL_URL, { account, wingetId: row.wingetId, kind: 'apply', deviceIds: [machine.deviceId] })).data;
      setOutcome({ action, name: row.shown, ...answer });
    } catch (ex) {
      setOutcome({ action, name: row.shown, error: errorText(ex) });
    } finally {
      setBusy(false);
      setActing(null);
    }
  };
  const problems = outcome ? [...list(outcome.refused), ...list(outcome.failed)] : [];
  const queuedCount = outcome ? list(outcome.queued).length : 0;

  return (
    <section aria-label="Policy">
      <div className="v2-bar">
        <h2 className="v2-h2">Software Policy for this Device</h2>
      </div>
      <div className="v2-bar">
        <input className="v2-input" type="search" aria-label="Filter policy" placeholder="Filter by name, publisher or version" value={typed} onChange={(e) => setTyped(e.target.value)} style={{ maxWidth: 300 }} />
        <span className="v2-sub" role="status">{state.state === 'ok' ? `${shown.length} of ${rows.length} policy rules` : ''}</span>
      </div>
      {outcome?.error && <div className="v2-alert err" role="alert">{outcome.error}</div>}
      {outcome && !outcome.error && (
        <div className={`v2-alert${problems.length ? ' info' : ''}`} role="status">
          {`${queuedCount} ${ACTIONS[outcome.action].toLowerCase()} job${queuedCount === 1 ? '' : 's'} of ${outcome.name} in the queue.`}
          {list(outcome.queued).map((q) => <div className="v2-sub" key={q.id}>{`${q.name}: ${q.id}`}</div>)}
          {problems.map((p) => <div className="v2-sub" key={`p${p.key}${p.error}`}>{`Not queued, ${p.name}: ${p.error}`}</div>)}
        </div>
      )}
      {state.state === 'loading' && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>}
      {state.state === 'error' && <div className="v2-alert err">{state.error}</div>}
      {state.state === 'noresult' && <div className="v2-empty">This machine has not reported its installed software yet.</div>}
      {state.state === 'ok' && (rows.length === 0 ? <div className="v2-empty">No policy rule names software on this machine.</div> : (
        <table className="v2-table" aria-label="Policy rules">
          <thead>
            <tr>
              <SortHeader k="name" sort={sort} onSort={toggle}>Application</SortHeader>
              <SortHeader k="policy" sort={sort} onSort={toggle}>Policy</SortHeader>
              <SortHeader k="status" sort={sort} onSort={toggle}>Status</SortHeader>
              <SortHeader k="version" sort={sort} onSort={toggle}>Installed version</SortHeader>
              <SortHeader k="available" sort={sort} onSort={toggle}>Newest</SortHeader>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {ordered.map((r, i) => (
              <tr key={`${r.shown}-${r.version}-${r.scope}-${i}`}>
                <td>
                  <div className="name" data-testid="policy-name" style={{ fontWeight: 700 }}>{r.shown}</div>
                  <div className="v2-sub">{[r.publisher, r.reason, r.decidedBy ? `Set by ${r.decidedBy}` : null].filter(Boolean).join(' - ')}</div>
                </td>
                <td>{r.policy}</td>
                <td>{r.status !== 'notinstalled' && <span className={`v2-pill ${statusOf(r.status).tone}`} title={statusOf(r.status).meaning}>{statusOf(r.status).label}</span>}</td>
                <td>{r.installed ? r.version : 'Not installed'}</td>
                <td>{r.available || ''}</td>
                <td className="nowrap">
                  {policyActions(r).map((a) => (
                    <button key={a.action} type="button" className="v2-btn" style={{ marginRight: 6 }} aria-label={labelOf(a.action, r)} disabled={busy || Boolean(a.why)} title={a.why}
                      onClick={() => setActing({ row: r, action: a.action })}>{ACTIONS[a.action]}</button>
                  ))}
                </td>
              </tr>
            ))}
            {ordered.length === 0 && <tr><td colSpan={6}><div className="v2-empty">No policy rule matches the filter.</div></td></tr>}
          </tbody>
        </table>
      ))}

      {acting && (
        <div className="v2-dialog-host">
          <div className="v2-dialog" role="dialog" aria-label={`${ACTIONS[acting.action]} ${acting.row.shown}`}>
            <h2>{`${ACTIONS[acting.action]} ${acting.row.shown}?`}</h2>
            <p>{SENTENCE[acting.action](acting.row, machine.hostname)}</p>
            {acting.action === 'remove' && acting.row.scope === 'user' && <p className="v2-sub">This copy is installed for a user, so it is uninstalled in that user&apos;s own session. They have to be signed in at the machine; if they are not, the job&apos;s result says it was not uninstalled.</p>}
            <div className="actions">
              <button type="button" className="v2-btn" onClick={() => setActing(null)} disabled={busy}>Cancel</button>
              <button type="button" className="v2-btn primary" onClick={() => run(acting)} disabled={busy}>{ACTIONS[acting.action]}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
